import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import DialogTitle from 'src/common/components/DialogTitle';
import type { QrCheckinScanResult } from 'src/hooks/useQrCheckinScan';

interface Props {
  open: boolean;
  result: QrCheckinScanResult | null;
  onClose: () => void;
  onCheckInSingle: () => void;
  onCheckInAllForUser: () => void;
}

export default function QrCheckinActionDialog({
  open,
  result,
  onClose,
  onCheckInSingle,
  onCheckInAllForUser,
}: Props) {
  const { t } = useTranslation();

  if (!result) {
    return null;
  }

  const canCheckInAll = !!result.ownerUserId;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle id="qr-checkin-action-title" onClose={onClose}>
        {t('checkins.qrScanActionTitle')}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          {result.alreadyCheckedIn
            ? t('checkins.qrScanActionDescriptionCheckedIn', { name: result.participantName })
            : t('checkins.qrScanActionDescription', { name: result.participantName })}
        </Typography>
        {!canCheckInAll ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('checkins.qrNoAccountUser')}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', px: 3, pb: 2, gap: 1 }}>
        <Stack spacing={1} sx={{ width: '100%' }}>
          <Button
            variant="contained"
            onClick={onCheckInSingle}
            disabled={result.alreadyCheckedIn}
            fullWidth
          >
            {t('checkins.qrCheckInSingle')}
          </Button>
          <Button
            variant="outlined"
            onClick={onCheckInAllForUser}
            disabled={!canCheckInAll}
            fullWidth
          >
            {t('checkins.qrCheckInAllForUser')}
          </Button>
          <Button onClick={onClose} fullWidth>
            {t('base.cancel')}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
